import {css, StyleSheet} from 'aphrodite';
import * as PropTypes from 'prop-types';
import React from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {BlobLinkCollapsiblePreview} from 'notebook/data-tables/BlobLinkList';
import {restBlobToGraphQL} from 'schemas/tags/BlobUtils';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {GRAY} from 'ui-library/Colors';
import FilesPickerComponent from 'ui-library/FilesPickerComponent';
import Modal from 'ui-library/modals/Modal';

import {BenchlingBlob} from './BlobConstants';

export interface BlobInputModalProps {
  allowMultiple: boolean;
  onRequestClose(): void;
  onUploadFiles(files: Array<File>, filteredExistingBlobs: Array<BenchlingBlob>): JQueryPromise<void>;
  maxFiles?: number;
  fileTypes?: Array<string>;
  existingBlobs?: Array<BenchlingBlob>;
}

interface State {
  isImporting: boolean;
  blobs: Array<BenchlingBlob>;
}

export default class BlobInputModal extends React.Component<BlobInputModalProps> {
  override state: State = {
    isImporting: false,
    blobs: this.props.allowMultiple ? this.props.existingBlobs || [] : [],
  };

  _isMounted: boolean = false;

  static propTypes = {
    allowMultiple: PropTypes.bool.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onUploadFiles: PropTypes.func.isRequired,
    maxFiles: PropTypes.number,
    fileTypes: PropTypes.arrayOf(PropTypes.string.isRequired),
  };

  fileInput: FilesPickerComponent | null = null;

  override componentDidMount(): void {
    this._isMounted = true;
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
  }

  renderMaxFiles(): string {
    return this.props.maxFiles != null ? ` (max ${this.props.maxFiles})` : '';
  }

  private handleRequestSubmit = (): JQueryPromise<void> => {
    assertAndContinue(this.fileInput != null, 'Expected non-null fileInput');
    this.setState({isImporting: true});
    return this.fileInput!.handleUpload({
      allComplete: this.props.onRequestClose,
    }).always(() => {
      if (this._isMounted) {
        this.setState({isImporting: false});
      }
    });
  };

  private handleUpdateExistingBlobs = (blobs: Array<BenchlingBlob>): void => {
    this.setState({blobs});
  };

  private getButtonText = (): string => {
    if (!this.state.isImporting) {
      return !this.props.existingBlobs || this.state.blobs.length === this.props.existingBlobs.length
        ? 'Import'
        : 'Save';
    } else {
      return !this.props.existingBlobs || this.state.blobs.length === this.props.existingBlobs?.length
        ? 'Importing...'
        : 'Saving...';
    }
  };

  override render(): JSX.Element {
    const submitButtonTestAttrs = {component: 'BlobInputModal', key: ModalButtonKey.SUBMIT};
    return (
      <Modal
        enableRedesign={true}
        title={`Upload ${pluralize(this.props.allowMultiple ? 2 : 1, 'file')}${this.renderMaxFiles()}`}
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this.handleRequestSubmit}
        buttons={[
          {text: 'Close', cancel: true},
          !this.state.isImporting
            ? {text: this.getButtonText(), submit: true, dataTestAttrs: submitButtonTestAttrs}
            : {
                text: this.getButtonText(),
                submit: true,
                disabled: true,
                dataTestAttrs: submitButtonTestAttrs,
              },
        ]}
      >
        <FilesPickerComponent
          upload={(files: Array<File>) => {
            if (!this.props.allowMultiple) {
              assert(files.length <= 1, 'Not allowed to upload multiple files');
            }
            return this.props.onUploadFiles(files, this.state.blobs);
          }}
          multiple={this.props.allowMultiple}
          maxFiles={this.props.maxFiles}
          fileTypes={this.props.fileTypes}
          bulkUpload={true}
          ref={(node: FilesPickerComponent) => {
            this.fileInput = node;
          }}
        />
        <ExistingBlobManager
          existingBlobs={this.state.blobs}
          updateExistingBlobs={this.handleUpdateExistingBlobs}
        />
      </Modal>
    );
  }
}

interface ExistingBlobManagerProps {
  existingBlobs: Array<BenchlingBlob>;
  updateExistingBlobs: (blobs: Array<BenchlingBlob>) => void;
}

function ExistingBlobManager(props: ExistingBlobManagerProps): JSX.Element | null {
  if (!props.existingBlobs || props.existingBlobs.length === 0) {
    return null;
  }
  return (
    <div>
      <h5 className="js-files-title">Existing Files ({props.existingBlobs.length})</h5>
      {props.existingBlobs.map((blob, index) => (
        <div key={blob.id} className={css(existingBlobManagerStyles.blobRow)}>
          <div className={css(existingBlobManagerStyles.blobWrapper)}>
            <BlobLinkCollapsiblePreview {...restBlobToGraphQL(blob)} owningEntityApiId="" />
          </div>
          <div className={css(existingBlobManagerStyles.deleteWrapper)}>
            <span
              className="glyphicon glyphicon-trash delete u-danger-pointer"
              onClick={() => {
                const newBlobs = [...props.existingBlobs];
                newBlobs.splice(index, 1);
                props.updateExistingBlobs(newBlobs);
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

const existingBlobManagerStyles = StyleSheet.create({
  blobWrapper: {
    'flex-grow': '1',
    'min-width': '0',
  },
  deleteWrapper: {
    padding: '8px 0 5px 8px',
  },
  blobRow: {
    display: 'flex',
    padding: '8px',
    border: `1px solid ${GRAY}`,
  },
});
